import { err } from '@src/services/common/Result';
import { EMPTY_STRUCTURE } from '@src/types.ts/story';
import { folderTrail, UNASSIGNED_FOLDER_NAME } from '@src/utility/folderTrail';
import { LogManager } from '@src/utility/Logger';
import { computed, makeAutoObservable, observable, runInAction } from 'mobx';

import type { Result } from '@src/services/common/Result';
import type {
  FolderMutationOk,
  FolderService,
} from '@src/services/FolderService';
import type { Folder, FolderStructure } from '@src/types.ts/story';

const logger = LogManager.getLogger('store:FolderStructureStore');

// Sentinel `currentFolderId` for the virtual "Unassigned" folder — the
// grouping of stories that aren't in any real folder. It never appears in the
// persisted structure; navigation into it goes through `enterUnassigned()`.
export const UNASSIGNED_FOLDER_ID = '__unassigned__';

export class FolderStructureStore {
  structure: FolderStructure = EMPTY_STRUCTURE;
  currentFolderId: string | null = null;
  isMutating = false;
  showcaseId: string | null = null;
  // Tracks the showcaseId for which `structure` reflects committed data
  // (either an upstream hydration or a successful mutation). Used to ignore
  // late re-binds with stale upstream snapshots that would overwrite live
  // mutation state, while still allowing the very first non-null hydration
  // to land when `selectedOrganization.folderStructure` arrives after mount.
  hydratedFor: string | null = null;
  // The showcaseId whose last load failed, so a caller waiting on hydration
  // can stop waiting instead of spinning forever.
  loadFailedFor: string | null = null;
  // One fetch per showcase at a time: the Dashboard starts the load as soon as
  // the org is known, and the folder view asks again when it mounts.
  private inFlight: { showcaseId: string; promise: Promise<void> } | null =
    null;

  constructor(private readonly folderService: FolderService) {
    makeAutoObservable<FolderStructureStore, 'inFlight'>(this, {
      inFlight: false,
      structure: observable.ref,
      foldersById: computed,
      foldersByParent: computed,
      storyCountByFolder: computed,
    });
  }

  /**
   * Bind the store to a showcase. Idempotent in the sense that:
   *   - Switching showcaseId resets navigation + structure to empty.
   *   - For a given showcaseId, the FIRST non-null upstream snapshot is
   *     accepted (handles GraphQL data that arrives after mount).
   *   - Subsequent re-binds for the same showcaseId are no-ops, so a
   *     parent re-render passing back a stale snapshot won't overwrite a
   *     locally-mutated structure.
   */
  bindToShowcase(
    showcaseId: string,
    initialStructure: FolderStructure | null | undefined,
  ): void {
    this.resetForShowcase(showcaseId);
    if (this.hydratedFor !== showcaseId && initialStructure) {
      this.structure = initialStructure;
      this.hydratedFor = showcaseId;
    }
  }

  loadStructure(showcaseId: string): Promise<void> {
    this.resetForShowcase(showcaseId);
    // A refetch over a hydrated structure would be discarded on commit anyway.
    if (this.hydratedFor === showcaseId) return Promise.resolve();
    if (this.inFlight?.showcaseId === showcaseId) return this.inFlight.promise;
    // A retry is a fresh wait: callers holding a loading state keep holding it.
    this.loadFailedFor = null;
    const promise = this.fetchStructure(showcaseId);
    this.inFlight = { showcaseId, promise };
    return promise;
  }

  private async fetchStructure(showcaseId: string): Promise<void> {
    const result = await this.folderService.getFolderStructure(showcaseId);
    if (this.inFlight?.showcaseId === showcaseId) this.inFlight = null;
    runInAction(() => {
      // Only commit if the showcase hasn't changed underneath us, and a local
      // mutation hasn't already populated the structure.
      if (this.showcaseId !== showcaseId) return;
      if (this.hydratedFor === showcaseId) return;
      if (result.ok) {
        this.structure = result.value;
        this.hydratedFor = showcaseId;
        this.loadFailedFor = null;
        logger.info('Folder structure loaded', {
          showcaseId,
          folderCount: result.value.folders.length,
        });
      } else {
        this.loadFailedFor = showcaseId;
        logger.warn('Failed to load folder structure', result.error);
      }
    });
  }

  private resetForShowcase(showcaseId: string): void {
    if (this.showcaseId === showcaseId) return;
    this.showcaseId = showcaseId;
    this.currentFolderId = null;
    this.hydratedFor = null;
    this.loadFailedFor = null;
    this.structure = EMPTY_STRUCTURE;
  }

  get foldersById(): Map<string, Folder> {
    return new Map(this.structure.folders.map(f => [f.id, f] as const));
  }

  get foldersByParent(): Map<string | null, Folder[]> {
    const map = new Map<string | null, Folder[]>();
    for (const f of this.structure.folders) {
      const bucket = map.get(f.parentId);
      if (bucket) bucket.push(f);
      else map.set(f.parentId, [f]);
    }
    for (const bucket of map.values()) {
      bucket.sort((a, b) => a.position - b.position);
    }
    return map;
  }

  get storyCountByFolder(): Map<string, number> {
    const map = new Map<string, number>();
    for (const fid of Object.values(this.structure.storyFolderMap)) {
      map.set(fid, (map.get(fid) ?? 0) + 1);
    }
    return map;
  }

  get currentSubfolders(): Folder[] {
    return this.foldersByParent.get(this.currentFolderId) ?? [];
  }

  get breadcrumb(): Folder[] {
    return this.breadcrumbForFolder(this.currentFolderId);
  }

  breadcrumbForFolder(folderId: string | null): Folder[] {
    if (folderId == null) return [];
    if (folderId === UNASSIGNED_FOLDER_ID) {
      return [
        {
          id: UNASSIGNED_FOLDER_ID,
          name: UNASSIGNED_FOLDER_NAME,
          parentId: null,
          position: 0,
          createdAt: '',
        },
      ];
    }
    const { trail, cycleAt } = folderTrail(this.foldersById, folderId);
    if (cycleAt) {
      logger.warn('Folder breadcrumb truncated due to cycle', {
        folderId: cycleAt,
      });
    }
    return trail;
  }

  folderIdForStory(storyId: string): string | null {
    return this.structure.storyFolderMap[storyId] ?? null;
  }

  storyCountForFolder(folderId: string): number {
    return this.storyCountByFolder.get(folderId) ?? 0;
  }

  subfolderCountForFolder(folderId: string): number {
    return this.foldersByParent.get(folderId)?.length ?? 0;
  }

  hasSiblingNameClash(
    name: string,
    parentId: string | null,
    ignoreFolderId?: string,
  ): boolean {
    const target = name.trim().toLowerCase();
    if (!target) return false;
    const siblings = this.foldersByParent.get(parentId) ?? [];
    return siblings.some(
      f => f.id !== ignoreFolderId && f.name.trim().toLowerCase() === target,
    );
  }

  enterFolder(folderId: string): void {
    if (!this.foldersById.has(folderId)) {
      logger.warn('enterFolder ignored — folder not found', { folderId });
      return;
    }
    this.currentFolderId = folderId;
  }

  // Navigate into the virtual "Unassigned" grouping (stories with no folder).
  enterUnassigned(): void {
    this.currentFolderId = UNASSIGNED_FOLDER_ID;
  }

  goToParent(): void {
    if (this.currentFolderId == null) return;
    const current = this.foldersById.get(this.currentFolderId);
    this.currentFolderId = current?.parentId ?? null;
  }

  goToRoot(): void {
    this.currentFolderId = null;
  }

  goToBreadcrumb(folderId: string | null): void {
    if (folderId == null) {
      this.currentFolderId = null;
      return;
    }
    this.currentFolderId = this.foldersById.has(folderId) ? folderId : null;
  }

  async createFolder(name: string): Promise<Result<FolderMutationOk>> {
    const showcaseId = this.showcaseId;
    if (!showcaseId) {
      return err('PRECONDITION', 'No showcase bound to folder store');
    }
    return this.runMutation(() =>
      this.folderService.createFolder(showcaseId, name, this.currentFolderId),
    );
  }

  async renameFolder(
    folderId: string,
    name: string,
  ): Promise<Result<FolderMutationOk>> {
    const showcaseId = this.showcaseId;
    if (!showcaseId) {
      return err('PRECONDITION', 'No showcase bound to folder store');
    }
    return this.runMutation(() =>
      this.folderService.renameFolder(showcaseId, folderId, name),
    );
  }

  async deleteFolder(
    folderId: string,
    mode: 'cascade' | 'promote',
  ): Promise<Result<FolderMutationOk>> {
    const showcaseId = this.showcaseId;
    if (!showcaseId) {
      return err('PRECONDITION', 'No showcase bound to folder store');
    }
    // Capture the ancestor chain BEFORE the mutation so we can walk to the
    // nearest surviving ancestor afterwards (rather than falling to root).
    const ancestorIds = this.ancestorChainOfCurrent();
    return this.runMutation(
      () => this.folderService.deleteFolder(showcaseId, folderId, mode),
      ancestorIds,
    );
  }

  async moveStoryToFolder(
    storyId: string,
    folderId: string | null,
  ): Promise<Result<FolderMutationOk>> {
    const showcaseId = this.showcaseId;
    if (!showcaseId) {
      return err('PRECONDITION', 'No showcase bound to folder store');
    }
    return this.runMutation(() =>
      this.folderService.moveStoryToFolder(showcaseId, storyId, folderId),
    );
  }

  async moveStoriesToFolder(
    storyIds: string[],
    folderId: string | null,
  ): Promise<Result<FolderMutationOk>> {
    const showcaseId = this.showcaseId;
    if (!showcaseId) {
      return err('PRECONDITION', 'No showcase bound to folder store');
    }
    if (storyIds.length === 0) {
      return err('VALIDATION', 'No stories selected');
    }
    return this.runMutation(() =>
      this.folderService.moveStoriesToFolder(showcaseId, storyIds, folderId),
    );
  }

  private ancestorChainOfCurrent(): string[] {
    const chain: string[] = [];
    if (this.currentFolderId == null) return chain;
    const byId = this.foldersById;
    let cursor = byId.get(this.currentFolderId);
    const guard = new Set<string>();
    while (cursor && !guard.has(cursor.id)) {
      guard.add(cursor.id);
      chain.push(cursor.id);
      cursor = cursor.parentId ? byId.get(cursor.parentId) : undefined;
    }
    return chain;
  }

  private async runMutation(
    fn: () => Promise<Result<FolderMutationOk>>,
    fallbackAncestors?: string[],
  ): Promise<Result<FolderMutationOk>> {
    runInAction(() => {
      this.isMutating = true;
    });
    try {
      const result = await fn();
      if (result.ok) {
        runInAction(() => {
          this.structure = result.value.structure;
          this.hydratedFor = this.showcaseId;
          this.reconcileCurrentFolder(fallbackAncestors);
        });
      } else {
        logger.warn('Folder mutation failed', result.error);
      }
      return result;
    } finally {
      runInAction(() => {
        this.isMutating = false;
      });
    }
  }

  // After the structure changes, ensure currentFolderId still points at a
  // real folder. If it doesn't, walk the pre-mutation ancestor chain to find
  // the nearest surviving ancestor (so a delete drops us at the deleted
  // folder's parent rather than at root). Falls back to root if none survive.
  private reconcileCurrentFolder(fallbackAncestors: string[] = []): void {
    if (this.currentFolderId == null) return;
    const byId = this.foldersById;
    if (byId.has(this.currentFolderId)) return;
    for (const id of fallbackAncestors) {
      if (byId.has(id)) {
        this.currentFolderId = id;
        return;
      }
    }
    this.currentFolderId = null;
  }
}
